import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  TextField,
  TablePagination,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

interface Resumen {
  id: number;
  nombreArchivo: string;
  nombreReferencia: string;
  fechaSubida: string;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface ComprobanteEgresoProps {
  onNavigate?: (path: string, params?: { archivoId?: string; archivoNombre?: string }) => void;
}

export const ComprobanteEgreso: React.FC<ComprobanteEgresoProps> = ({ onNavigate }) => {
  const [resumenes, setResumenes] = useState<Resumen[]>([]);
  const [totalResumenes, setTotalResumenes] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nombreReferencia, setNombreReferencia] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableLoading, setTableLoading] = useState(true); // Nuevo estado para la carga de la tabla

  const fetchResumenes = useCallback(async () => {
    setTableLoading(true); // Iniciar carga
    setError(null);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm,
      });
      const response = await fetch(`http://localhost:3002/api/resumenes?${params}`);
      const data = await response.json();
      console.log('Datos recibidos del backend:', data);
      if (data.success) {
        console.log('Resúmenes:', data.data);
        setResumenes(data.data);
        setTotalResumenes(data.total);
      }
    } catch (err) {
      setError('No se pudieron cargar los resúmenes. ¿El servidor backend está encendido?');
    } finally {
      setTableLoading(false); // Terminar carga
    }
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchResumenes();
  }, [fetchResumenes]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      // Sugerir un nombre de referencia basado en el nombre del archivo (sin extensión)
      const nombreSinExtension = file.name.replace('.pdf', '');
      setNombreReferencia(nombreSinExtension);
      setError(null);
      setSuccess(null);
    } else {
      setSelectedFile(null);
      setNombreReferencia('');
      setError('Por favor, selecciona un archivo PDF.');
    }
  };

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = (error) => reject(error);
    });

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setError('No hay ningún archivo seleccionado.');
      return;
    }

    if (!nombreReferencia.trim()) {
      setError('Por favor, ingresa un nombre de referencia.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const responseExiste = await fetch(`http://localhost:3002/api/resumenes/${selectedFile.name}/existe`);
      const dataExiste = await responseExiste.json();

      if (dataExiste.data.existe) {
        setError('Ya se ha subido un archivo con este nombre.');
        setLoading(false);
        return;
      }

      const contenido_base64 = await toBase64(selectedFile);
      const responseSubida = await fetch('http://localhost:3002/api/resumenes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nombre_archivo: selectedFile.name,
          nombre_referencia: nombreReferencia.trim(),
          contenido_base64,
        }),
      });

      if (!responseSubida.ok) {
        const errorData = await responseSubida.json();
        throw new Error(errorData.error || 'Error al subir el archivo.');
      }

      setSuccess('¡Archivo subido con éxito!');
      setSelectedFile(null);
      setNombreReferencia('');
      await fetchResumenes();
      setIsModalOpen(false);
    } catch (err: any) {
      setError(err.message || 'Ocurrió un error inesperado.');
    } finally {
      setLoading(false);
    }
  }, [selectedFile, nombreReferencia, fetchResumenes]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset page to 1 on new search
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEliminarResumen = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      try {
        const response = await fetch(`http://localhost:3002/api/resumenes/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          setSuccess('Registro eliminado exitosamente');
          await fetchResumenes();
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Error al eliminar el registro');
        }
      } catch (err: any) {
        setError(err.message || 'Error al eliminar el registro');
      }
    }
  };

  const handleCrearComprobante = (resumen: Resumen) => {
    if (onNavigate) {
      onNavigate('/contabilidad/movimientos-egreso', {
        archivoId: resumen.id.toString(),
        archivoNombre: resumen.nombreArchivo
      });
    } else {
      // Fallback si no hay función de navegación
      alert(`Crear comprobante de egreso para: ${resumen.nombreReferencia || resumen.nombreArchivo}`);
      console.log('Crear comprobante para:', resumen);
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Informes Bancarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setIsModalOpen(true)}
        >
          Subir Archivo
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Buscar por nombre de archivo"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre del Archivo</TableCell>
              <TableCell>Nombre de Referencia</TableCell>
              <TableCell>Fecha de Subida</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                  <Typography>Cargando...</Typography>
                </TableCell>
              </TableRow>
            ) : resumenes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No se encontraron registros.
                </TableCell>
              </TableRow>
            ) : (
              resumenes.map((resumen) => (
                <TableRow key={resumen.id}>
                  <TableCell>{resumen.id}</TableCell>
                  <TableCell>{resumen.nombreArchivo || ''}</TableCell>
                  <TableCell>{resumen.nombreReferencia || ''}</TableCell>
                  <TableCell>
                    {resumen.fechaSubida ? 
                      new Date(resumen.fechaSubida).toLocaleString('es-ES', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 
                      'Sin fecha'
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        onClick={() => handleCrearComprobante(resumen)}
                        color="primary"
                        size="small"
                        title="Crear Comprobante de Egreso"
                      >
                        <ReceiptIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEliminarResumen(resumen.id)}
                        color="error"
                        size="small"
                        title="Eliminar Registro"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalResumenes}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFile(null);
          setNombreReferencia('');
          setError(null);
          setSuccess(null);
        }}
        aria-labelledby="modal-title"
      >
        <Box sx={style}>
          <Typography id="modal-title" variant="h6" component="h2">
            Subir Nuevo Informe
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Button
              component="label"
              role={undefined}
              variant="outlined"
              startIcon={<UploadIcon />}
              sx={{ minWidth: 200 }}
            >
              Seleccionar PDF
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileChange}
                accept="application/pdf"
              />
            </Button>

            {selectedFile && (
              <>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Archivo seleccionado: <strong>{selectedFile.name}</strong>
                </Typography>
                
                <TextField
                  fullWidth
                  label="Nombre de referencia"
                  value={nombreReferencia}
                  onChange={(e) => setNombreReferencia(e.target.value)}
                  placeholder="Ingresa un nombre para identificar este archivo"
                  sx={{ mt: 2 }}
                  helperText="Este nombre te ayudará a identificar fácilmente el archivo"
                />
              </>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile || loading}
              sx={{ minWidth: 200, marginTop: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Subir Archivo'}
            </Button>

            {error && (
              <Alert severity="error" sx={{ width: '100%', marginTop: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ width: '100%', marginTop: 2 }}>
                {success}
              </Alert>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

